import { useCallback, useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";

import { Grid } from "@material-ui/core";

import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import StoreField from "components/_refactor/fields/store";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import UtilsFncs from "utils/UtilsFncs";
import Api from "lib/api/httpRequestHelper";
import { updateFilters } from "redux/reducers/comercialStructure";
import MaskAndValid from "utils/maskAndValid";
import useStructureQueries from "hooks/useStructureQueries";

const getSelectedDefaultValue = (selected, options, idKey) => {
  if (
    selected === null ||
    selected === undefined ||
    !options ||
    options.length === 0
  )
    return null;
  const selectedNormalized =
    typeof selected === "string" && selected !== "TODAS"
      ? parseInt(selected, 10)
      : selected;
  return options.find((option) => option[idKey] === selectedNormalized);
};

const updateFieldOptions = (
  options,
  setFieldOptions,
  setSelectedValue,
  key,
  urlSearch,
  skipSingleOptionSelect = false
) => {
  return new Promise((resolve) => {
    setFieldOptions(options);
    if (urlSearch) {
      const defaultSelected = getSelectedDefaultValue(urlSearch, options, key);
      if (defaultSelected) {
        setSelectedValue(defaultSelected[key]);
      }
    } else if (!skipSingleOptionSelect && options.length === 1) {
      setSelectedValue(options[0][key]);
    }
    resolve(true);
  });
};

const brandList = [
  { IdMarca: 1, Nome: "Karsten" },
  { IdMarca: 2, Nome: "Trussardi" },
  { IdMarca: 3, Nome: "Karsten Decor" },
];

export default function StructureFields({
  onStructureFieldsChange,
  disableAll,
  onStructureFieldsLoad,
  requiredFields,
  hideFields = [],
  clearFilters,
  showSingleStore,
  removeBrandAll,
  singleStoreId,
  ownStore,
  defaultBrand,
  defaultEconomicGroup,
  defaultStore,
  selectFirstOption,
  franquias = false,
  fieldsOrder = [
    "brand",
    "channel",
    "representative",
    "economicGroup",
    "store",
    "storeSingle",
  ],
}) {
  const location = useLocation();
  const dispatch = useDispatch();
  const snackbar = useSnackbar();
  const LSBrands = removeBrandAll
    ? brandList
    : JSON.parse(localStorage.getItem("usuarioMarcas")) || [];

  const brands = useMemo(() => LSBrands, []);
  const [channels, setChannels] = useState([]);
  const [representatives, setRepresentatives] = useState([]);
  const [economicGroups, setEconomicGroups] = useState([]);
  const [stores, setStores] = useState([]);

  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [selectedRepresentative, setSelectedRepresentative] = useState(null);
  const [selectedEconomicGroup, setSelectedEconomicGroup] = useState(null);
  const [selectedStore, setSelectedStore] = useState([]);
  const [singleStore, setSingleStore] = useState(singleStoreId || "");

  const [loadingFields, setLoadingFields] = useState([]);
  const [disabledFields, setDisabledFields] = useState([]);
  const fieldsCache = useSelector((state) => state.comercialStructure.filters);
  const { getEconomicGroups, getStores, getChannels, getRepresentatives, clearCache } = useStructureQueries();

  const isLoading = (field) => loadingFields?.includes(field);
  const isLoadingBrand = isLoading("brand");
  const isLoadingRepresentative = isLoading("representative");
  const isLoadingChannel = isLoading("channel");
  const isLoadingEconomicGroup = isLoading("economicGroup");
  const isLoadingStore = isLoading("store");
  const isLoadingSingleStore = isLoading("storeSingle");

  const handleFieldsCache = useCallback(
    (key, value) => {
      const userVision = localStorage.getItem("userVision");
      const fields = { ...fieldsCache };
      if (!userVision) {
        fields[key] = value;
        dispatch(updateFilters(fields));
      }
    },
    [dispatch, fieldsCache]
  );

  const isFieldHidden = (field) => hideFields.includes(field);
  const handleLoadingFields = (loadingFields) => {
    setLoadingFields(loadingFields);
    onStructureFieldsLoad(loadingFields);
  };
  const isDisabled = (field) => disabledFields?.includes(field);
  const isRequired = (field) => requiredFields?.includes(field);

  const handleRefreshChannel = useCallback(async () => {
    handleLoadingFields(["channel"]);
    await fetchChannelList(null, true);
    handleLoadingFields([]);
  }, []);

  const handleRefreshRepresentative = useCallback(async () => {
    const params = {
      idMarca: selectedBrand,
      idCanal: selectedChannel,
    };
    handleLoadingFields(["representative"]);
    await fetchRepresentativeList(params, null, true);
    handleLoadingFields([]);
  }, [selectedBrand, selectedChannel]);

  const handleRefreshEconomicGroup = useCallback(async () => {
    const params = {
      idMarca: selectedBrand,
      idCanal: selectedChannel,
      idRepresentante: selectedRepresentative,
    };
    handleLoadingFields(["economicGroup", "store", "storeSingle"]);
    await Promise.all([
      fetchEconomicGroupList(params, null, true),
      fetchStoreList({
        ...params,
        idGrupoEconomico: selectedEconomicGroup,
      }, null, true),
    ]);
    handleLoadingFields([]);
  }, [selectedBrand, selectedChannel, selectedRepresentative, selectedEconomicGroup]);

  const handleRefreshStore = useCallback(async () => {
    const params = {
      idMarca: selectedBrand,
      idCanal: selectedChannel,
      idRepresentante: selectedRepresentative,
      idGrupoEconomico: selectedEconomicGroup,
    };
    handleLoadingFields(["store", "storeSingle"]);
    await fetchStoreList(params, null, true);
    handleLoadingFields([]);
  }, [selectedBrand, selectedChannel, selectedRepresentative, selectedEconomicGroup]);

  async function fetchChannelList(urlSearch, forceRefresh = false) {
    const categoryEndpoint = "channel";
    if (isFieldHidden(categoryEndpoint)) return;
    
    if (forceRefresh) {
      clearCache("channel", {});
    } else {
      try {
        const result = await getChannels();
        if (result?.data) {
          handleFieldsCache(categoryEndpoint, result.data);
          await updateFieldOptions(
            result.data,
            setChannels,
            setSelectedChannel,
            "IdCanal",
            urlSearch
          );

          if (selectedChannel && !result.data.find((item) => item?.IdCanal === selectedChannel)) {
            setSelectedChannel(null);
          }

          return;
        }
      } catch (error) {
        console.error("Erro ao buscar canais do cache:", error);
      }
    }

    const userVision = localStorage.getItem("userVision");
    if (!userVision && fieldsCache[categoryEndpoint]) {
      const cachedData = fieldsCache[categoryEndpoint];
      await updateFieldOptions(
        cachedData,
        setChannels,
        setSelectedChannel,
        "IdCanal",
        urlSearch
      );

      if (selectedChannel && !cachedData.find((item) => item?.IdCanal === selectedChannel)) {
        setSelectedChannel(null);
      }

      return;
    }

    try {
      const value = await Api.get(
        categoryEndpoint,
        "commercialstructure",
        "",
        true
      );
      if (value?.Result) {
        handleFieldsCache(categoryEndpoint, value.Result);
        await updateFieldOptions(
          value.Result,
          setChannels,
          setSelectedChannel,
          "IdCanal",
          urlSearch
        );

        if (selectedChannel && !value.Result.find((item) => item?.IdCanal === selectedChannel)) {
          setSelectedChannel(null);
        }
      }
    } catch (error) {
      snackbar.showMessage(
        "Ocorreu um erro ao buscar canais. Verifique os parâmetros informados.",
        { severity: "error" }
      );
    }
  }

  async function fetchRepresentativeList(params, urlSearch, forceRefresh = false) {
    const categoryEndpoint = "representative";
    if (isFieldHidden(categoryEndpoint)) return;
    
    const normalizedParams = { ...params };
    if (normalizedParams.idMarca === "TODAS" || normalizedParams.idMarca === 0 || normalizedParams.idMarca === "0") {
      normalizedParams.idMarca = undefined;
    }
    
    const requestParams = UtilsFncs.RestParamsComposer(normalizedParams);
    const cacheKey = `${categoryEndpoint}${requestParams}`;
    
    if (forceRefresh) {
      clearCache(categoryEndpoint, normalizedParams);
    } else {
      try {
        const result = await getRepresentatives(normalizedParams);
        if (result?.data) {
          handleFieldsCache(cacheKey, result.data);
          await updateFieldOptions(
            result.data,
            setRepresentatives,
            setSelectedRepresentative,
            "Id",
            urlSearch
          );

          if (selectedRepresentative && !result.data.find((item) => item?.Id === selectedRepresentative)) {
            setSelectedRepresentative(null);
          }

          if (!urlSearch && result.data?.length === 1) {
            const brandId = result.data[0]?.IdMarca ?? (brands.length === 1 ? brands[0]?.IdMarca : null);
            if (brandId != null) {
              setSelectedBrand(brandId);
            }
          } else if (!urlSearch && result.data?.length > 1) {
            setSelectedBrand(0);
          }

          return;
        }
      } catch (error) {
        console.error("Erro ao buscar representantes do cache:", error);
      }
    }

    const userVision = localStorage.getItem("userVision");
    if (!userVision && fieldsCache[cacheKey]) {
      const cachedData = fieldsCache[cacheKey];
      await updateFieldOptions(
        cachedData,
        setRepresentatives,
        setSelectedRepresentative,
        "Id",
        urlSearch
      );

      if (!urlSearch && cachedData?.length === 1) {
        const brandId = cachedData[0]?.IdMarca ?? (brands.length === 1 ? brands[0]?.IdMarca : null);
        if (brandId != null) {
          setSelectedBrand(brandId);
        }
      } else if (!urlSearch && cachedData?.length > 1) {
        setSelectedBrand(0);
      }

      if (selectedRepresentative && !cachedData.find((item) => item?.Id === selectedRepresentative)) {
        setSelectedRepresentative(null);
      }

      return;
    }

    try {
      const value = await Api.get(
        categoryEndpoint,
        "commercialstructure",
        requestParams,
        true
      );
      if (value?.Result) {
        handleFieldsCache(cacheKey, value.Result);
        await updateFieldOptions(
          value.Result,
          setRepresentatives,
          setSelectedRepresentative,
          "Id",
          urlSearch
        );

        if (selectedRepresentative && !value.Result.find((item) => item?.Id === selectedRepresentative)) {
          setSelectedRepresentative(null);
        }

        if (!urlSearch && value.Result?.length === 1) {
          const brandId = value.Result[0]?.IdMarca ?? (brands.length === 1 ? brands[0]?.IdMarca : null);
          if (brandId != null) {
            setSelectedBrand(brandId);
          }
        } else if (!urlSearch && value.Result?.length > 1) {
          setSelectedBrand(0);
        }
      }
    } catch (error) {
      snackbar.showMessage(
        "Ocorreu um erro ao buscar representantes. Verifique os parâmetros informados.",
        { severity: "error" }
      );
    }
  }

  async function fetchEconomicGroupList(params, urlSearch, forceRefresh = false) {
    const categoryEndpoint = "econnomical-group";
    if (isFieldHidden(categoryEndpoint)) return;
    
    const normalizedParams = { ...params };
    if (normalizedParams.idMarca === "TODAS" || normalizedParams.idMarca === 0 || normalizedParams.idMarca === "0") {
      normalizedParams.idMarca = undefined;
    }
    
    const requestParams = UtilsFncs.RestParamsComposer(normalizedParams);
    const cacheKey = `${categoryEndpoint}${requestParams}`;
    
    if (forceRefresh) {
      clearCache(categoryEndpoint, normalizedParams);
    } else {
      try {
        const result = await getEconomicGroups(normalizedParams);
        if (result?.data) {
          handleFieldsCache(cacheKey, result.data);
          await updateFieldOptions(
            result.data,
            setEconomicGroups,
            setSelectedEconomicGroup,
            "Id",
            urlSearch
          );

          if(selectedEconomicGroup && !result.data.find((item) => item?.Id === selectedEconomicGroup)) {
            setSelectedEconomicGroup(null);
          }

          return;
        }
      } catch (error) {
        console.error("Erro ao buscar grupos econômicos do cache:", error);
      }
    }

    const userVision = localStorage.getItem("userVision");
    if (!userVision && fieldsCache[cacheKey]) {
      const cachedData = fieldsCache[cacheKey];
      await updateFieldOptions(
        cachedData,
        setEconomicGroups,
        setSelectedEconomicGroup,
        "Id",
        urlSearch
      );

      if (selectedEconomicGroup && !cachedData.find((item) => item?.Id === selectedEconomicGroup)) {
        setSelectedEconomicGroup(null);
      }

      return;
    }

    try {
      const value = await Api.get(
        categoryEndpoint,
        "commercialstructure",
        requestParams,
        true
      );
      if (value?.Result) {
        handleFieldsCache(cacheKey, value.Result);
        await updateFieldOptions(
          value.Result,
          setEconomicGroups,
          setSelectedEconomicGroup,
          "Id",
          urlSearch
        );

        if (selectedEconomicGroup && !value.Result.find((item) => item?.Id === selectedEconomicGroup)) {
          setSelectedEconomicGroup(null);
        }
      }
    } catch (error) {
      snackbar.showMessage(
        "Ocorreu um erro ao buscar grupos econômicos. Verifique os parâmetros informados.",
        { severity: "error" }
      );
    }
  }

  async function fetchStoreList(params, urlSearch, forceRefresh = false) {
    const categoryEndpoint = "store";
    if (isFieldHidden(categoryEndpoint) && !showSingleStore) return;
    
    const normalizedParams = { ...params };
    if (normalizedParams.idMarca === 0 || normalizedParams.idMarca === "0") {
      normalizedParams.idMarca = undefined;
    }
    
    const requestParams = UtilsFncs.RestParamsComposer(normalizedParams);
    const cacheKey = `${categoryEndpoint}${requestParams}`;
    
    if (forceRefresh) {
      clearCache(categoryEndpoint, normalizedParams);
    } else {
      try {
        const result = await getStores(normalizedParams);
        if (result?.data) {
          handleFieldsCache(cacheKey, result.data);
          await updateFieldOptions(
            result.data,
            setStores,
            setSelectedStore,
            "Id",
            urlSearch,
            true
          );

          if (selectedStore && result.data) {
            const resultIds = result.data.map((item) => item?.Id);
            if (Array.isArray(selectedStore) && selectedStore.length > 0) {
              const filtered = selectedStore.filter((item) =>
                resultIds.includes(item?.Id)
              );
              if (filtered.length !== selectedStore.length) {
                setSelectedStore(filtered);
              }
            } else if (!resultIds.includes(selectedStore)) {
              setSelectedStore([]);
            }
          }

          return;
        }
      } catch (error) {
        console.error("Erro ao buscar lojas do cache:", error);
      }

      const userVision = localStorage.getItem("userVision");
      if (!userVision && fieldsCache[cacheKey]) {
        const cachedData = fieldsCache[cacheKey];
        await updateFieldOptions(
          cachedData,
          setStores,
          setSelectedStore,
          "Id",
          urlSearch,
          true
        );

        if (selectedStore && cachedData) {
          const resultIds = cachedData.map((item) => item?.Id);
          if (Array.isArray(selectedStore) && selectedStore.length > 0) {
            const filtered = selectedStore.filter((item) =>
              resultIds.includes(item?.Id)
            );
            if (filtered.length !== selectedStore.length) {
              setSelectedStore(filtered);
            }
          } else if (!resultIds.includes(selectedStore)) {
            setSelectedStore([]);
          }
        }

        return;
      }
    }

    try {
      const value = await Api.get(
        categoryEndpoint,
        "commercialstructure",
        requestParams,
        true
      );
      if (value?.Result) {
        handleFieldsCache(cacheKey, value.Result);
        await updateFieldOptions(
          value.Result,
          setStores,
          setSelectedStore,
          "Id",
          urlSearch,
          true
        );

        if (selectedStore && value.Result) {
          const resultIds = value.Result.map((item) => item?.Id);
          if (Array.isArray(selectedStore) && selectedStore.length > 0) {
            const filtered = selectedStore.filter((item) =>
              resultIds.includes(item?.Id)
            );
            if (filtered.length !== selectedStore.length) {
              setSelectedStore(filtered);
            }
          } else if (!resultIds.includes(selectedStore)) {
            setSelectedStore([]);
          }
        }
      }
    } catch (error) {
      snackbar.showMessage(
        "Ocorreu um erro ao buscar lojas. Verifique os parâmetros informados.",
        { severity: "error" }
      );
    }
  }

  async function fetchFranquiasEconomicGroupsAndStores(
    urlSearchEconomicGroup,
    urlSearchStore
  ) {
    if (
      isFieldHidden("economicGroup") &&
      isFieldHidden("store") &&
      !showSingleStore
    )
      return;

    try {
      const response = await Api.get(
        "list-economic-groups",
        "franquias",
        "?idCanal=6",
        true
      );

      if (response?.Result?.Items) {
        const economicGroupsFormatted = response.Result.Items.map((item) => ({
          Id: item.IdGrupoEconomico,
          Nome: item.Nome,
        }));

        const allStoresFormatted = response.Result.Items.reduce((acc, item) => {
          if (item.Lojas && item.Lojas.length > 0) {
            const storesFormatted = item.Lojas.map((loja) => ({
              Id: loja.IdPessoa,
              Nome: loja.Nome,
              Apelido: loja.Apelido,
              CNPJ: loja.CnpjCpf,
              Cidade: loja.Cidade,
              Uf: loja.Uf,
            }));
            return [...acc, ...storesFormatted];
          }
          return acc;
        }, []);

        await updateFieldOptions(
          economicGroupsFormatted,
          setEconomicGroups,
          setSelectedEconomicGroup,
          "Id",
          urlSearchEconomicGroup
        );

        await updateFieldOptions(
          allStoresFormatted,
          setStores,
          setSelectedStore,
          "Id",
          urlSearchStore
        );
      }
    } catch (error) {
      snackbar.showMessage(
        "Ocorreu um erro ao buscar grupos econômicos e lojas de franquias.",
        { severity: "error" }
      );
    }
  }

  const allFields = [
    "brand",
    "channel",
    "representative",
    "economicGroup",
    "store",
    "storeSingle",
  ];

  const onBrandChange = async (newValue) => {
    const newBrandId = newValue?.IdMarca ?? 0;

    setSelectedBrand(newBrandId);

    handleLoadingFields(allFields.slice(1));
    setDisabledFields(allFields);

    try {
      const params = {
        idMarca: newBrandId,
        idCanal: selectedChannel,
        idRepresentante: selectedRepresentative,
        idGrupoEconomico: selectedEconomicGroup,
      };

      if (franquias) {
        await Promise.all([fetchFranquiasEconomicGroupsAndStores()]);
      } else {
        await Promise.all([
          fetchChannelList(),
          fetchRepresentativeList(params),
          fetchEconomicGroupList(params),
          fetchStoreList(params),
        ]);
      }
    } catch (error) {
      console.error("Falha ao atualizar campos dependentes da marca.", error);
    } finally {
      handleLoadingFields([]);
      setDisabledFields([]);
    }
  };

  const onChannelChange = async (newValue) => {
    const newChannelId = newValue?.IdCanal || null;
    setSelectedChannel(newChannelId);

    setSelectedRepresentative(null);
    setSelectedEconomicGroup(null);
    setSelectedStore([]);

    const dependentFields = [
      "representative",
      "economicGroup",
      "store",
      "storeSingle",
    ];
    handleLoadingFields(dependentFields);
    setDisabledFields(allFields);

    try {
      const params = {
        idMarca: selectedBrand,
        idCanal: newChannelId,
        idRepresentante: selectedRepresentative,
        idGrupoEconomico: selectedEconomicGroup,
      };

      if (franquias) {
        await fetchFranquiasEconomicGroupsAndStores();
      } else {
        await Promise.all([
          fetchRepresentativeList(params),
          fetchEconomicGroupList(params),
          fetchStoreList(params),
        ]);
      }
    } catch (error) {
      console.error("Falha ao atualizar campos dependentes do canal.", error);
    } finally {
      handleLoadingFields([]);
      setDisabledFields([]);
    }
  };

  const onRepresentativeChange = async (newValue) => {
    const newRepId = newValue?.Id || null;
    setSelectedRepresentative(newRepId);

    setSelectedEconomicGroup(null);
    setSelectedStore([]);

    const dependentFields = ["economicGroup", "store", "storeSingle"];
    handleLoadingFields(dependentFields);
    setDisabledFields(allFields);

    try {
      const params = {
        idMarca: selectedBrand,
        idCanal: selectedChannel,
        idRepresentante: newRepId,
        idGrupoEconomico: selectedEconomicGroup,
      };

      if (franquias) {
        await fetchFranquiasEconomicGroupsAndStores();
      } else {
        await Promise.all([
          fetchEconomicGroupList(params),
          fetchStoreList(params),
        ]);
      }
    } catch (error) {
      console.error(
        "Falha ao atualizar campos dependentes do representante.",
        error
      );
    } finally {
      handleLoadingFields([]);
      setDisabledFields([]);
    }
  };

  const onEconomicGroupChange = async (newValue) => {
    const newGroupId = newValue?.Id || null;
    setSelectedEconomicGroup(newGroupId);

    const dependentFields = ["store", "storeSingle"];
    handleLoadingFields(dependentFields);
    setDisabledFields(allFields);

    try {
      if (franquias) {
        if (newGroupId) {
          try {
            const response = await Api.get(
              "list-economic-groups",
              "franquias",
              "?idCanal=6",
              true
            );

            if (response?.Result?.Items) {
              const selectedGroup = response.Result.Items.find(
                (item) => item.IdGrupoEconomico === newGroupId
              );

              if (selectedGroup && selectedGroup.Lojas) {
                const storesFormatted = selectedGroup.Lojas.map((loja) => ({
                  Id: loja.IdPessoa,
                  Nome: loja.Nome,
                  Apelido: loja.Apelido,
                  CNPJ: loja.CnpjCpf,
                  Cidade: loja.Cidade,
                  Uf: loja.Uf,
                }));

                setStores(storesFormatted);
              }
            }
          } catch (error) {
            snackbar.showMessage(
              "Ocorreu um erro ao buscar lojas do grupo econômico.",
              { severity: "error" }
            );
            console.log(error);
          }
        } else {
          await fetchFranquiasEconomicGroupsAndStores();
        }
      } else {
        const params = {
          idMarca: selectedBrand,
          idCanal: selectedChannel,
          idRepresentante: selectedRepresentative,
          idGrupoEconomico: newGroupId,
        };
        await fetchStoreList(params);
      }
    } catch (error) {
      console.error(
        "Falha ao atualizar campos dependentes do grupo econômico.",
        error
      );
    } finally {
      handleLoadingFields([]);
      setDisabledFields([]);
    }
  };

  const onStoreChange = (newValue) => {
    if (newValue?.length === 0) setSelectedStore([]);
    if (newValue?.length && newValue !== "all") setSelectedStore(newValue);
  };

  const onStoreChangeSingle = (newValue) => {
    setSingleStore(newValue?.Id || "");
  };

  useEffect(() => {
    handleLoadingFields(allFields);
    const urlParams = UtilsFncs.GetUrlSearchParams(location.search);

    const initialBrand = defaultBrand
      ? defaultBrand
      : brands.length === 1
      ? brands[0]
      : null;
    const initialBrandId = urlParams.idMarca
      ? urlParams.idMarca
      : initialBrand?.IdMarca;

    if (
      initialBrandId !== undefined &&
      initialBrandId !== null &&
      initialBrandId !== ""
    ) {
      setSelectedBrand(
        initialBrandId === "TODAS" ? 0 : parseInt(initialBrandId, 10)
      );
    }

    const params = {
      idMarca: initialBrandId,
      idCanal: urlParams.idCanal,
      idRepresentante: urlParams.idRepresentante,
      idGrupoEconomico: urlParams.idGrupoEconomico,
    };

    const brandAndChannelHidden =
      hideFields.includes("brand") && hideFields.includes("channel");
    const storeParamsForInitialLoad = brandAndChannelHidden
      ? { idMarca: "TODAS", idGrupoEconomico: urlParams.idGrupoEconomico }
      : params;

    if (franquias) {
      Promise.all([
        fetchChannelList(urlParams.idCanal),
        fetchRepresentativeList(params, urlParams.idRepresentante),
        fetchFranquiasEconomicGroupsAndStores(
          urlParams.idGrupoEconomico,
          urlParams.idLoja
        ),
      ]).finally(() => {
        handleLoadingFields([]);
      });
    } else {
      Promise.all([
        fetchChannelList(urlParams.idCanal),
        fetchRepresentativeList(params, urlParams.idRepresentante),
        fetchEconomicGroupList(params, urlParams.idGrupoEconomico),
        fetchStoreList(storeParamsForInitialLoad, urlParams.idLoja),
      ]).finally(() => {
        handleLoadingFields([]);
      });
    }
  }, [location]);

  useEffect(() => {
    const newStoreIds = selectedStore?.map((item) => item?.Id).join(",") || "";

    onStructureFieldsChange({
      idMarca: selectedBrand === 0 ? "TODAS" : selectedBrand,
      idCanal: selectedChannel,
      idRepresentante: selectedRepresentative,
      idGrupoEconomico: selectedEconomicGroup,
      idLoja: newStoreIds,
      idLojaUnica: singleStore,
    });
  }, [
    selectedBrand,
    selectedChannel,
    selectedRepresentative,
    selectedEconomicGroup,
    selectedStore,
    singleStore,
    onStructureFieldsChange,
  ]);

  useEffect(() => {
    const verifySelection = (list, selectedId, idKey, setSelection) => {
      if (selectedId && list.length > 0) {
        const stillExists = list.some((item) => item[idKey] === selectedId);
        if (!stillExists) setSelection(null);
      }
    };

    const verifyMultiSelection = (list, selectedItems, idKey, setSelection) => {
      if (selectedItems?.length > 0 && list.length > 0) {
        const existingIds = new Set(list.map((item) => item[idKey]));
        const validSelection = selectedItems.filter((item) =>
          existingIds.has(item[idKey])
        );
        if (validSelection.length !== selectedItems.length) {
          setSelection(validSelection);
        }
      } else if (selectedItems?.length > 0 && list.length === 0) {
        setSelection([]);
      }
    };

    verifySelection(channels, selectedChannel, "IdCanal", setSelectedChannel);
    verifySelection(
      representatives,
      selectedRepresentative,
      "Id",
      setSelectedRepresentative
    );
    verifySelection(
      economicGroups,
      selectedEconomicGroup,
      "Id",
      setSelectedEconomicGroup
    );
    verifyMultiSelection(stores, selectedStore, "Id", setSelectedStore);
  }, [channels, representatives, economicGroups, stores]);

  useEffect(() => {
    if (
      stores.length === 1 &&
      (!selectedStore?.length || selectedStore[0]?.Id !== stores[0]?.Id)
    ) {
      setSelectedStore(stores);
    }
  }, [representatives, stores, selectedStore]);

  useEffect(() => {
    if (showSingleStore && stores.length === 1 && !singleStore) {
      setSingleStore(stores[0]?.Id || "");
    }
  }, [showSingleStore, stores, singleStore]);

  useEffect(() => {
    if (clearFilters) {
      handleLoadingFields(allFields);

      setSelectedBrand(null);
      setSelectedChannel(null);
      setSelectedRepresentative(null);
      setSelectedEconomicGroup(null);
      setSingleStore("");
      setSelectedStore([]);

      if (franquias) {
        Promise.all([fetchFranquiasEconomicGroupsAndStores()]).finally(() => {
          handleLoadingFields([]);
        });
      } else {
        Promise.all([
          fetchChannelList(),
          fetchRepresentativeList(),
          fetchEconomicGroupList(),
          fetchStoreList({ idMarca: "TODAS" }),
        ]).finally(() => {
          handleLoadingFields([]);
        });
      }
    }
  }, [clearFilters]);

  const handleVerifyChannel = () => {
    let verifyChannel = false;
    if (channels.length !== 0 && selectedChannel) {
      channels?.map((channel) => {
        if (channel.IdCanal === selectedChannel) {
          return (verifyChannel = true);
        }
      });
    }
    if (verifyChannel === false) {
      setSelectedChannel("");
    }
  };

  const handleVerifyRepresentatives = () => {
    let verifyRepresentative = false;
    if (representatives.length !== 0 && selectedRepresentative) {
      representatives?.map((representative) => {
        if (representative.Id === selectedRepresentative) {
          return (verifyRepresentative = true);
        }
      });
    }

    if (verifyRepresentative === false) {
      setSelectedRepresentative("");
    }
  };

  const handleVerifyEconomicsGroups = () => {
    let verifyEconomicGroup = false;
    if (economicGroups.length !== 0 && selectedEconomicGroup) {
      economicGroups?.map((economic) => {
        if (economic.Id === selectedEconomicGroup) {
          return (verifyEconomicGroup = true);
        }
      });
    }
    if (verifyEconomicGroup === false && economicGroups.length > 1) {
      setSelectedEconomicGroup("");
    }
  };

  const handleVerifyStores = () => {
    let verifyStore = false;
    const listSelected = [];
    if (stores?.length !== 0 && selectedStore) {
      stores?.map((store) => {
        if (
          selectedStore?.length !== 0 &&
          selectedStore?.length !== undefined
        ) {
          selectedStore?.map((selected) => {
            if (store.Id === selected.Id) {
              listSelected.push(selected);
              return (verifyStore = true);
            }
          });
        }
      });
      setSelectedStore(listSelected);
    }
    if (verifyStore === false && stores.length > 1) {
      setSelectedStore([]);
    }
  };

  function filterStores(stores) {
    return stores.filter(
      (store) => store.Nome && store.Nome.includes("KARSTEN")
    );
  }

  useEffect(() => {
    handleVerifyStores();
    handleVerifyEconomicsGroups();
    handleVerifyRepresentatives();
    handleVerifyChannel();
  }, [selectedBrand, channels, representatives, economicGroups, stores]);

  useEffect(() => {
    if (
      defaultEconomicGroup &&
      economicGroups.length > 0 &&
      !selectedEconomicGroup
    ) {
      const defaultGroupId = Array.isArray(defaultEconomicGroup)
        ? defaultEconomicGroup[0]
        : defaultEconomicGroup;

      const defaultGroup = economicGroups.find(
        (group) => group.Id === defaultGroupId
      );

      if (defaultGroup) {
        setSelectedEconomicGroup(defaultGroupId);
        onEconomicGroupChange(defaultGroup, true);
      }
    }
  }, [defaultEconomicGroup, economicGroups]);

  useEffect(() => {
    if (defaultStore && stores.length > 0 && selectedStore.length === 0) {
      const defaultStoreIds = Array.isArray(defaultStore)
        ? defaultStore
        : [defaultStore];

      const defaultStores = stores.filter((store) =>
        defaultStoreIds.includes(store.Id)
      );

      if (defaultStores.length > 0) {
        setSelectedStore(defaultStores);
      }
    }
  }, [defaultStore, stores]);

  const fieldsMap = {
    brand: !isFieldHidden("brand") && (
      <Grid item xs={12} md={6} lg={3} key="brand">
        <AutoCompleteField
          options={brands}
          onChange={onBrandChange}
          label="Marca"
          optionIdKey="IdMarca"
          disabled={disableAll || isDisabled("brand")}
          loading={isLoadingBrand}
          value={
            selectedBrand !== null && selectedBrand !== undefined
              ? getSelectedDefaultValue(selectedBrand, brands, "IdMarca")
              : defaultBrand !== null && defaultBrand !== undefined
              ? getSelectedDefaultValue(defaultBrand, brands, "IdMarca")
              : brands?.length === 1
              ? brands[0]
              : ""
          }
          required={isRequired("brand")}
        />
      </Grid>
    ),
    channel: !isFieldHidden("channel") && (
      <Grid item xs={12} md={6} lg={3} key="channel">
        <AutoCompleteField
          options={channels}
          label="Canal"
          optionIdKey="IdCanal"
          onChange={onChannelChange}
          disabled={
            disableAll ||
            isDisabled("channel") ||
            channels?.length === 1 ||
            channels?.length === 0
          }
          loading={isLoadingChannel}
          value={
            selectedChannel
              ? getSelectedDefaultValue(selectedChannel, channels, "IdCanal")
              : channels?.length === 1
              ? channels[0]
              : ""
          }
          required={isRequired("channel")}
        />
      </Grid>
    ),
    representative: !isFieldHidden("representative") && (
      <Grid item xs={12} md={6} lg={3} key="representative">
        <AutoCompleteField
          options={representatives}
          onChange={onRepresentativeChange}
          label="Representante"
          disabled={
            disableAll ||
            isDisabled("representative") ||
            representatives?.length === 1 ||
            representatives?.length === 0
          }
          loading={isLoadingRepresentative}
          value={
            selectedRepresentative
              ? getSelectedDefaultValue(
                  selectedRepresentative,
                  representatives,
                  "Id"
                )
              : representatives?.length === 1 ||
                selectFirstOption?.includes("representative")
              ? representatives[0]
              : ""
          }
          required={isRequired("representative")}
        />
      </Grid>
    ),
    economicGroup: !isFieldHidden("economicGroup") && (
      <Grid item xs={12} md={6} lg={3} key="economicGroup">
        <AutoCompleteField
          options={economicGroups}
          label="Grupo Econômico"
          onChange={onEconomicGroupChange}
          disabled={
            disableAll ||
            isDisabled("economicGroup") ||
            economicGroups?.length === 1 ||
            economicGroups?.length === 0
          }
          loading={isLoadingEconomicGroup}
          value={
            selectedEconomicGroup
              ? getSelectedDefaultValue(
                  selectedEconomicGroup,
                  economicGroups,
                  "Id"
                )
              : defaultEconomicGroup
              ? getSelectedDefaultValue(
                  defaultEconomicGroup,
                  economicGroups,
                  "Id"
                )
              : economicGroups?.length === 1 ||
                selectFirstOption?.includes("economicGroup")
              ? economicGroups[0]
              : ""
          }
          required={isRequired("economicGroup")}
          showRefreshButton={true}
          onRefresh={handleRefreshEconomicGroup}
        />
      </Grid>
    ),
    store: !isFieldHidden("store") && (
      <Grid item xs={12} md={6} key="store">
        <StoreField
          value={selectedStore}
          storeList={!ownStore ? stores : filterStores(stores)}
          disabled={disableAll || isDisabled("store") || stores?.length === 0}
          onChange={onStoreChange}
          loading={isLoadingStore}
          required={isRequired("store")}
        />
      </Grid>
    ),
    storeSingle: showSingleStore && !isFieldHidden("storeSingle") && (
      <Grid item xs={12} md={6} key="storeSingle">
        <AutoCompleteField
          options={stores?.map((store) => ({
            Id: store?.Id,
            Nome: `${store?.Nome} | ${MaskAndValid.maskCNPJ(store?.CNPJ)}`,
            CNPJ: store?.CNPJ,
            Apelido:
              store?.Apelido !== " " || store?.Apelido?.length >= 2
                ? `${store?.Apelido} | ${MaskAndValid.maskCNPJ(store?.CNPJ)}`
                : `${store?.Nome} | ${MaskAndValid.maskCNPJ(store?.CNPJ)}`,
          }))}
          label="Loja"
          onChange={onStoreChangeSingle}
          disabled={
            disableAll ||
            isDisabled("store") ||
            stores.length === 0 ||
            stores.length === 1
          }
          loading={isLoadingSingleStore}
          value={
            singleStore
              ? getSelectedDefaultValue(singleStore, stores, "Id")
              : stores.length === 1
              ? stores[0]
              : ""
          }
          required={isRequired("storeSingle")}
          customTooltipText={(option) => {
            return option?.Apelido;
          }}
          showRefreshButton={true}
          onRefresh={handleRefreshStore}
          ignoreCnpjPunctuationInSearch={true}
        />
      </Grid>
    ),
  };

  const renderFields = () => {
    const defaultOrder = [
      "brand",
      "channel",
      "representative",
      "economicGroup",
      "store",
      "storeSingle",
    ];

    const completeOrder = [
      ...fieldsOrder,
      ...defaultOrder.filter((field) => !fieldsOrder.includes(field)),
    ];

    return completeOrder
      .filter((field) => fieldsMap[field])
      .map((field) => fieldsMap[field]);
  };

  return <>{renderFields()}</>;
}
